import { Tag, TagColor } from "react-migration/components/Tag";
import { EnrichedComparableDTO } from "react-migration/lib/typings/EnrichedComparables";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { getZooplaAccessLevel } from "react-migration/domains/comparables/util/getZooplaAccessLevel";
import { getFormattedPropertyType } from "react-migration/domains/comparables/util/PropertyTypes";

import { useMemo } from "react";
import { useSnapshot } from "valtio";
import { userStore } from "src/js/stores/user/store";
import Feature from "src/js/stores/user/Feature";

export const OwnershipTags = ({
  transaction,
  status,
}: {
  transaction: EnrichedComparableDTO;
  status?: "for_sale" | "rent_under_offer" | "rented" | "sale_under_offer" | "sold" | "to_rent" | "to_hide";
}) => {
  const { t } = useTranslation();
  const { tenure, is_new_property } = transaction;
  const zooplaTenure = transaction.zooplaEnrichment?.attributes?.tenure;
  const zooplaIsNewProperty = transaction.zooplaEnrichment?.attributes?.newBuild;
  const accessLevel = getZooplaAccessLevel();
  const { features } = useSnapshot(userStore.user);
  const isUSUser = features.includes(Feature.usAccess);

  const tenureType = () => {
    // US users don't have tenure data, but for backwards compatibility
    // they have F in the tenure field. So we short circuit here
    if (isUSUser) {
      return null;
    }

    if (zooplaTenure && accessLevel === "full") {
      return zooplaTenure;
    } else if (tenure === "L") {
      return "Leasehold";
    } else if (tenure === "F") {
      return "Freehold";
    }
    return null;
  };

  const propertyType = useMemo(() => {
    return getFormattedPropertyType(transaction, accessLevel, isUSUser);
  }, [transaction, accessLevel, isUSUser]);

  return (
    <div
      className="comps-listing-tags flex flex-row items-center justify-start gap-2 w-full overflow-x-auto"
      data-testid="comps-listing-tags"
      role="group"
      aria-label="Comparable property tags"
    >
      {tenureType() && <Tag tag={tenureType()} color={TagColor.GREEN} />}
      {(is_new_property || zooplaIsNewProperty) && <Tag tag="New build" color={TagColor.DEFAULT} />}

      {status && !isUSUser && (
        <Tag
          tag={t(`comparables.selection_details.status.${status}`)}
          color={TagColor.PURPLE}
          className={accessLevel !== "full" ? "blur-[3px]" : ""}
        />
      )}

      {transaction.designation && <Tag tag={transaction.designation} color={TagColor.DEFAULT} />}
      {propertyType && <Tag tag={propertyType} color={TagColor.LIGHT_GRAY} />}
    </div>
  );
};
